import type { PDFDocumentProxy } from 'pdfjs-dist';

const RENDER_SCALE = 3.5;
const MAX_CANVAS_SIZE_PX = 7000;
const JPEG_QUALITY = 0.87;

export interface PdfDocument {
    proxy: PDFDocumentProxy;
    pageCount: number;
    fileName: string;
    pageImages: Map<number, File>;
}

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

const loadPdfjs = (): Promise<typeof import('pdfjs-dist')> => {
    if (!pdfjsPromise) {
        pdfjsPromise = import('pdfjs-dist')
            .then((pdfjs) => {
                pdfjs.GlobalWorkerOptions.workerSrc = new URL(
                    'pdfjs-dist/build/pdf.worker.min.js',
                    import.meta.url,
                ).toString();
                return pdfjs;
            })
            .catch((error) => {
                pdfjsPromise = null;
                throw error;
            });
    }
    return pdfjsPromise;
};

export const openPdf = async (file: File): Promise<PdfDocument> => {
    const pdfjs = await loadPdfjs();
    const proxy = await pdfjs.getDocument({
        data: new Uint8Array(await file.arrayBuffer()),
        isEvalSupported: false,
    }).promise;
    return { proxy, pageCount: proxy.numPages, fileName: file.name, pageImages: new Map() };
};

export const closePdf = (pdf: PdfDocument): void => {
    pdf.proxy.destroy().catch((error) => {
        console.warn('Could not destroy the PDF document', error);
    });
};

export const getPdfPageImage = async (pdf: PdfDocument, pageNumber: number): Promise<File> => {
    const cachedPage = pdf.pageImages.get(pageNumber);
    if (cachedPage) {
        return cachedPage;
    }

    const page = await pdf.proxy.getPage(pageNumber);
    const viewport = page.getViewport({ scale: RENDER_SCALE });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Could not create a canvas context for the PDF page');
    }

    const width = Math.floor(viewport.width);
    const height = Math.floor(viewport.height);
    const outputScale = Math.min(1, MAX_CANVAS_SIZE_PX / Math.max(width, height));
    canvas.width = width * outputScale;
    canvas.height = height * outputScale;

    await page.render({
        canvasContext: context,
        viewport,
        transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
    }).promise;

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) {
        throw new Error('Could not convert the rendered PDF page to an image');
    }

    const pageFileName = `${pdf.fileName.replace(/\.pdf$/i, '')}-page-${pageNumber}.jpg`;
    const pageImage = new File([blob], pageFileName, { type: blob.type });
    pdf.pageImages.set(pageNumber, pageImage);
    return pageImage;
};
